import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowUp,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";

import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@stockline/ui/components/table";
import { ScrollArea, ScrollBar } from "@stockline/ui/components/scroll-area";
import type {
  ColumnarTable,
  TableColumnRole,
  TableConfig,
} from "@stockline/shared/types/bi";
import { BI_TABLE_MAX_PX } from "@stockline/shared/types/bi";
import {
  isNumericCell,
  toChartNumber,
} from "@stockline/shared/validation/bi/numeric";
import { cn } from "@/lib/utils";
import {
  deltaSign,
  formatDateCell,
  displayUnit,
  formatDelta,
  formatNumber,
  isDateCell,
  isNumericColumn,
  prettifyColumn,
} from "./format";
import type { ChartVariant } from "./types";

interface DataTableProps {
  table: ColumnarTable;
  config: TableConfig;
  title?: string;
  /** "card" fills the widget card body and scrolls within it; "default" (chat)
   *  grows to fit its content. Both cap the scroll viewport at MAX_TABLE_PX. */
  variant?: ChartVariant;
  /** BI results carry raw `snake_case` DB column names, so headers are
   *  prettified by default. Markdown tables already arrive with human-written
   *  headers — pass `false` there so "YoY" doesn't become "Yoy". A per-column
   *  `label` in `config.columns` always wins over prettification. */
  prettifyHeaders?: boolean;
  /** Extra classes for the root element. Used by the widget card to fade a
   *  freshly-computed table in (the one widget type that otherwise appears
   *  instantly) — kept to a plain opacity fade so the delicate flex sizing
   *  below is untouched. */
  className?: string;
}

type SortState = { col: string; dir: "asc" | "desc" };

/** A resolved, ready-to-render column: its source index in the raw table plus
 *  the presentation the config asked for (or auto-detected). */
interface DisplayColumn {
  key: string;
  label: string;
  role?: TableColumnRole;
  unit?: string;
  /** Source column index into each raw data row. */
  colIdx: number;
  /** Right-align + tabular numerals (a value/delta role, or an auto-detected
   *  numeric column when no role was assigned). */
  numeric: boolean;
  /** Fixed decimal count every numeric cell in the column renders at (the max
   *  precision observed, capped at 2), so the decimal points line up down the
   *  column instead of "41 088,51" sitting over "20 991,2" over "0". Undefined
   *  for non-numeric columns. */
  fractionDigits?: number;
}

const MAX_ROWS = 100;
/** Tallest a table's scroll viewport may grow before it scrolls vertically
 *  instead of pushing the page — the unbounded chat variant only; a `card` is
 *  bounded by its grid slot, so the cap is not applied there (it would clip a
 *  slot taller than this and leave dead space below). Shared with the backend
 *  height-derivation so the two agree on the cap. Beyond this the body scrolls
 *  (y only). */
const MAX_TABLE_PX = BI_TABLE_MAX_PX;

/** Decimal places a cell value carries ("79875.8" → 1, 42 → 0), capped at the
 *  2 the renderer ever shows. Reads the raw pg string/number, so "4512.00"
 *  still counts as 2 — the SQL asked for cents, render cents. */
function cellFractionDigits(value: string | number | null): number {
  if (value === null || value === undefined) return 0;
  const match = /\.(\d+)/.exec(String(value));
  return match ? Math.min(match[1].length, 2) : 0;
}

/** A truthy summary-row flag cell (1 / "1" / "true"). The flag column is
 *  numeric-ish by convention (0/1), but tolerate booleans-as-text. */
function isSummaryFlag(value: string | number | null): boolean {
  if (value === null || value === undefined) return false;
  if (isNumericCell(value)) return toChartNumber(value) !== 0;
  return String(value).trim().toLowerCase() === "true";
}

export function DataTable({
  table,
  config,
  title,
  variant = "default",
  prettifyHeaders = true,
  className,
}: Readonly<DataTableProps>) {
  const { t } = useTranslation();
  // User-initiated sort: first click asc, second desc, third clears (original
  // order). Sorting the FULL dataset (before the MAX_ROWS slice) keeps the
  // "top 100" meaningful once a column is sorted.
  const [sort, setSort] = useState<SortState | null>(null);

  const columns = useMemo(() => table?.columns ?? [], [table]);
  const rows = table?.data ?? [];

  // Index of the summary-row FLAG column (STK-1672): a boolean-ish column the
  // SQL emits to mark total/summary rows. Never displayed; drives row emphasis
  // and keeps totals out of user sorts.
  const summaryColIdx = config.summary_column
    ? columns.indexOf(config.summary_column)
    : -1;

  const isSummaryRow = (row: (string | number | null)[]) =>
    summaryColIdx >= 0 && isSummaryFlag(row[summaryColIdx]);

  const sortedData = useMemo(() => {
    const allRows = table?.data ?? [];
    if (!sort) return allRows;
    const colIdx = (table?.columns ?? []).indexOf(sort.col);
    if (colIdx < 0) return allRows;
    // Summary rows (totals) don't participate in a user sort — interleaving a
    // "Total S26" between two days is meaningless. They keep their relative
    // order and sink below the sorted data rows.
    const summaryIdx = config.summary_column
      ? (table?.columns ?? []).indexOf(config.summary_column)
      : -1;
    const dataRows =
      summaryIdx >= 0
        ? allRows.filter((row) => !isSummaryFlag(row[summaryIdx]))
        : allRows;
    const summaryRows =
      summaryIdx >= 0
        ? allRows.filter((row) => isSummaryFlag(row[summaryIdx]))
        : [];
    const numeric = isNumericColumn(dataRows.map((row) => row[colIdx]));
    const dir = sort.dir === "asc" ? 1 : -1;
    const sorted = [...dataRows].sort((ra, rb) => {
      const a = ra[colIdx];
      const b = rb[colIdx];
      const aBlank = a === null || a === undefined || a === "";
      const bBlank = b === null || b === undefined || b === "";
      // Blanks always sort last, regardless of direction.
      if (aBlank || bBlank) {
        if (aBlank === bBlank) return 0;
        return aBlank ? 1 : -1;
      }
      const cmp = numeric
        ? toChartNumber(a) - toChartNumber(b)
        : String(a).localeCompare(String(b), undefined, { numeric: true });
      return cmp * dir;
    });
    return [...sorted, ...summaryRows];
  }, [table, sort, config.summary_column]);

  const card = variant === "card";

  // Resolve which columns to render, in order, with their presentation.
  // Precedence (STK-808):
  //  1. `config.columns` (typed + labelled) — drives order, labels, roles.
  //  2. `config.visible_columns` — order only, auto-formatted (legacy).
  //  3. all columns — auto-formatted.
  // A column is right-aligned when its role is value/delta, or (no role) when
  // its cells auto-detect as numeric. Numeric detection reads the visible slice.
  // The MAX_ROWS cap bounds DATA rows only — a summary row is never truncated
  // away. A total is the ANSWER the table was built to show, not a sample of
  // it, so dropping "Total S26" while keeping 100 arbitrary days is backwards.
  // It also went silent: sorting pins summaries last (see `sortedData`), so on
  // a >=100-row table every total fell off the end of this slice the instant a
  // user clicked a header — and the "Showing 100 of 150 rows" note said exactly
  // the same thing before and after, so nothing announced the loss.
  const visibleRows = useMemo(() => {
    if (summaryColIdx < 0) return sortedData.slice(0, MAX_ROWS);
    let dataKept = 0;
    return sortedData.filter((row) =>
      isSummaryFlag(row[summaryColIdx]) ? true : dataKept++ < MAX_ROWS,
    );
  }, [sortedData, summaryColIdx]);

  const displayColumns = useMemo<DisplayColumn[]>(() => {
    const autoLabel = (key: string) =>
      prettifyHeaders ? prettifyColumn(key) : key;
    const detectNumeric = (colIdx: number) =>
      isNumericColumn(visibleRows.map((row) => row[colIdx]));
    // Fixed per-column precision: the max decimals any visible cell carries
    // (capped at 2), so every cell renders at the same depth ("0,00" under
    // "20 991,20") and the decimal points align.
    const detectFractionDigits = (colIdx: number) =>
      visibleRows.reduce(
        (max, row) => Math.max(max, cellFractionDigits(row[colIdx])),
        0,
      );
    const withDigits = (col: Omit<DisplayColumn, "fractionDigits">) =>
      col.numeric
        ? { ...col, fractionDigits: detectFractionDigits(col.colIdx) }
        : col;
    // The summary-row flag column is renderer metadata, never content.
    const isFlagColumn = (key: string) =>
      !!config.summary_column && key === config.summary_column;

    const typed = config.columns ?? null;
    if (typed && typed.length > 0) {
      const resolved: DisplayColumn[] = [];
      for (const spec of typed) {
        if (isFlagColumn(spec.key)) continue;
        const colIdx = columns.indexOf(spec.key);
        if (colIdx < 0) continue; // config names a column the result lacks.
        const roleNumeric = spec.role === "value" || spec.role === "delta";
        resolved.push(
          withDigits({
            key: spec.key,
            label: spec.label ?? autoLabel(spec.key),
            role: spec.role,
            unit: spec.unit,
            colIdx,
            numeric: roleNumeric || detectNumeric(colIdx),
          }),
        );
      }
      if (resolved.length > 0) return resolved;
    }

    const keys = config.visible_columns ?? columns;
    return keys
      .filter((key) => !isFlagColumn(key))
      .map((key) => ({ key, colIdx: columns.indexOf(key) }))
      .filter((c) => c.colIdx >= 0)
      .map(({ key, colIdx }) =>
        withDigits({
          key,
          label: autoLabel(key),
          role: undefined,
          unit: undefined,
          colIdx,
          numeric: detectNumeric(colIdx),
        }),
      );
  }, [
    config.columns,
    config.visible_columns,
    config.summary_column,
    columns,
    visibleRows,
    prettifyHeaders,
  ]);

  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground text-sm p-4">
        {t("charts.noData", "No data to display")}
      </div>
    );
  }

  const toggleSort = (col: string) =>
    setSort((prev) => {
      if (prev?.col !== col) return { col, dir: "asc" };
      if (prev.dir === "asc") return { col, dir: "desc" };
      return null;
    });

  // Default (untyped) cell formatting: null → "-", booleans → Yes/No, ISO dates
  // → localized, numbers → grouped. Shared by every non-value/non-delta cell.
  const formatCell = (
    value: string | number | null,
    fractionDigits?: number,
  ): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    // Date/period buckets ("2026-06-15") render in the tenant locale before the
    // numeric check — an ISO date is not a numeric cell, so order only matters
    // for clarity. No tz conversion: these are calendar labels, not instants.
    if (isDateCell(value)) return formatDateCell(value);
    // Numbers AND pg numeric strings ("327760.17") format identically — same
    // numeric-cell predicate the charts + validator use, so a numeric column
    // never shows a raw unformatted string next to formatted numbers.
    if (isNumericCell(value)) {
      return formatNumber(toChartNumber(value), { fractionDigits });
    }
    return String(value);
  };

  // A `delta`-role cell: signed, coloured by direction with an arrow (▲ rise,
  // ▼ drop, muted em-dash for no change / non-numeric). Bare number — the
  // column's unit lives in the header, not in every cell.
  const renderDelta = (
    value: string | number | null,
    fractionDigits?: number,
  ) => {
    if (value === null || value === undefined || !isNumericCell(value)) {
      return <span className="text-muted-foreground">-</span>;
    }
    const n = toChartNumber(value);
    const sign = deltaSign(n);
    const Arrow = sign > 0 ? ArrowUp : ArrowDown;
    return (
      <span
        className={cn(
          "inline-flex items-center justify-end gap-0.5 font-medium",
          sign > 0 && "text-emerald-600 dark:text-emerald-400",
          sign < 0 && "text-red-600 dark:text-red-400",
          sign === 0 && "text-muted-foreground",
        )}
      >
        {sign !== 0 && <Arrow className="h-3.5 w-3.5 shrink-0" />}
        {formatDelta(n, undefined, { fractionDigits })}
      </span>
    );
  };

  const renderCell = (col: DisplayColumn, value: string | number | null) => {
    if (col.role === "delta") {
      return renderDelta(value, col.fractionDigits);
    }
    // A zero in a numeric column renders muted ("0,00" in light gray): a
    // closed day / empty depot shouldn't weigh as much as real revenue, and
    // the eye should glide to where business happened. Still a value — an
    // empty cell would be indistinguishable from missing data.
    const zero = isNumericCell(value) && toChartNumber(value) === 0;
    const wrapZero = (text: string) =>
      zero ? <span className="text-muted-foreground/70">{text}</span> : text;
    if (col.role === "value") {
      if (value === null || value === undefined || !isNumericCell(value)) {
        return formatCell(value, col.fractionDigits);
      }
      // Bare number: the unit renders ONCE in the column header ("CA (€)"),
      // not on every cell — a column of "12 € / 34 € / 56 €" repeats what the
      // reader already knows and buries the digits in symbols.
      return wrapZero(
        formatNumber(toChartNumber(value), {
          fractionDigits: col.fractionDigits,
        }),
      );
    }
    // Zero muting only applies to NUMERIC columns — a lone "0" in a text
    // column (a code, a label) keeps full contrast.
    if (col.numeric) {
      return wrapZero(formatCell(value, col.fractionDigits));
    }
    return formatCell(value, col.fractionDigits);
  };

  return (
    // In a card EVERY level sizes by flex-shrink and NEVER by a percentage
    // height; in the chat (default) the block grows to fit. WidgetCard caps a
    // table card with `max-h-full` and an auto height so it can hug a short
    // table — which leaves that height INDEFINITE, and a `h-full` child of an
    // indefinite parent silently resolves to its own content height. That is
    // how a tall table grew past the card and got amputated by the card's
    // `overflow-hidden` with no scrollbar to reach the lost rows. `min-h-0`
    // shrinks fine against an indefinite parent; `height: 100%` cannot.
    <div
      className={cn(
        card ? "flex min-h-0 flex-auto flex-col gap-2" : "space-y-2",
        className,
      )}
    >
      {title && !card && <h4 className="font-medium text-sm">{title}</h4>}
      <ScrollArea
        // `max-h` caps growth and the built-in vertical scrollbar then engages.
        // In a card we deliberately do NOT grow: `flex: 0 1 auto` lets the
        // bordered box hug its content when the table is short (no empty space
        // below the last row), while `min-h-0` lets it shrink and scroll
        // internally when the table is taller than the card body. Radix's own
        // viewport ships as `h-full` — the same percentage-of-an-indefinite-
        // parent trap as above — so it must be made a shrinkable flex child
        // too, or it renders at full content height inside this
        // `overflow-hidden` box and clips the tail rows instead of scrolling.
        className={cn(
          "w-full whitespace-nowrap",
          // The chat block carries its own frame. In a card it must NOT: the
          // card is already a bordered box, and a second one nested a pixel
          // inside it drew two rules down both sides and read as a box in a
          // box. The table runs full-bleed to the card's edges instead, and
          // the card's own `overflow-hidden` + radius clip it (STK-1672).
          !card && "rounded-md border",
          card &&
            "flex min-h-0 flex-col [&>[data-radix-scroll-area-viewport]]:min-h-0",
        )}
        style={card ? undefined : { maxHeight: MAX_TABLE_PX }}
      >
        {/* A plain <table>, deliberately NOT shadcn's <Table>: that component
            wraps its table in an `overflow-auto` div, which becomes the
            nearest scrollport for the sticky header below — and since that div
            is content-height it never scrolls, so the header pinned to a box
            with no scroll range and rode away with the real scrollport. It is
            also a redundant second scroll container: this ScrollArea already
            owns BOTH axes (see <ScrollBar orientation="horizontal" />), so
            dropping it also routes sideways scrolling through Radix's styled
            scrollbar instead of a bare nested one. Same classes it applied. */}
        <table className="w-full caption-bottom text-sm">
          <TableHeader>
            <TableRow>
              {displayColumns.map((col) => {
                const active = sort?.col === col.key;
                let SortIcon = ChevronsUpDown;
                let ariaSort: "ascending" | "descending" | "none" = "none";
                if (sort && active) {
                  const asc = sort.dir === "asc";
                  SortIcon = asc ? ChevronUp : ChevronDown;
                  ariaSort = asc ? "ascending" : "descending";
                }
                return (
                  <TableHead
                    key={col.key}
                    aria-sort={ariaSort}
                    // Sticky so the header stays visible while the body scrolls
                    // vertically inside the capped viewport; the background
                    // keeps rows from bleeding through underneath it — which is
                    // why the card's tint is `bg-muted` (opaque) and never
                    // `bg-muted/40`. Headers read as chrome, not data: smaller,
                    // uppercase, tracked — clearly distinct from the 14px cells
                    // below. In a card they shrink further (32px micro-caps on
                    // a tint, mirroring /dictionary's MappingTable) so the card
                    // title is unmistakably the ONE header and this is just the
                    // table's furniture — shadcn ships `h-12`, which no `py-*`
                    // overrides, so the height must be set outright (STK-1672).
                    // ⚠ `h-8` = 32px is MIRRORED by `BI_TABLE_HEADER_PX`
                    // (@stockline/shared) — the backend derives a table's grid
                    // height from it (STK-1676). The DataTable "drift guard"
                    // test pins the class ↔ constant; retune the constant if
                    // this height changes.
                    className={cn(
                      "group/head sticky top-0 z-10 bg-background text-xs uppercase tracking-wider",
                      card &&
                        "h-8 bg-muted px-3 py-0 text-[10px] font-semibold tracking-wide",
                      col.numeric && "text-right",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className={cn(
                        "inline-flex max-w-full items-center gap-1 font-medium hover:text-foreground",
                        col.numeric && "flex-row-reverse",
                      )}
                    >
                      {/* The unit is stated once, here — "CA (€)" — and the
                          cells below stay bare digits. `displayUnit` still
                          localizes the symbol (kg→lbs, €→$ by language). */}
                      <span className="truncate">
                        {col.unit
                          ? `${col.label} (${displayUnit(col.unit)})`
                          : col.label}
                      </span>
                      {/* The chevron on EVERY header is horizontal clutter, so
                          at rest only the ACTIVE sort shows its icon; the rest
                          reveal theirs on header hover / keyboard focus. */}
                      <SortIcon
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          active
                            ? "opacity-90"
                            : "opacity-0 transition-opacity group-hover/head:opacity-40 group-focus-within/head:opacity-40",
                        )}
                      />
                    </button>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row, rowIdx) => {
              const summary = isSummaryRow(row);
              return (
                <TableRow
                  key={rowIdx}
                  // A summary row ("Total S26") must not read as just another
                  // day — but a rule across the table reads as the table
                  // ENDING and a new one starting. The tint alone says "this
                  // row is different" without cutting the grid; the weight bump
                  // is the second, quieter signal. Deliberately no border here:
                  // the 2px rule this used to carry was the rupture (STK-1672).
                  // `/80`, not the `/40` this used to carry alongside its rule:
                  // the tint is now the ONLY signal, and at /40 (≈#fbfbfb on
                  // white) it was barely a band at all. `hover:bg-muted` keeps
                  // the row from going LIGHTER on hover — TableRow ships
                  // `hover:bg-muted/50`, which sits below this row's own tint.
                  className={cn(
                    summary && "bg-muted/80 font-medium hover:bg-muted",
                  )}
                  data-summary-row={summary || undefined}
                >
                  {displayColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        // Match the header's inset. shadcn ships `px-4` on a
                        // head and `p-2` on a cell, so a header sat 8px right
                        // of its own column — invisible while a bordered box
                        // and the card's padding framed the table, glaring once
                        // it runs full-bleed to the card's edge (STK-1672).
                        // ⚠ `py-1.5` feeds `BI_TABLE_ROW_PX` (@stockline/shared),
                        // the per-row height the backend sizes a table's slot by
                        // (STK-1676). The DataTable "drift guard" test pins it —
                        // retune that constant if this padding changes.
                        card && "px-3 py-1.5",
                        col.numeric && "text-right tabular-nums",
                        // The principal column is the row's subject — emphasise it
                        // so the eye lands on it first.
                        col.role === "main" && "font-medium text-foreground",
                      )}
                    >
                      {renderCell(col, row[col.colIdx])}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {table.data.length > MAX_ROWS && (
        // Carries its own inset in a card: the body runs full-bleed (padding 0)
        // so the table can reach the edges, which would otherwise leave this
        // note flush against them.
        <div
          className={cn(
            "shrink-0 text-xs text-muted-foreground",
            card && "px-3 pb-2",
          )}
        >
          {t("charts.showingRows", "Showing {{shown}} of {{total}} rows", {
            shown: MAX_ROWS,
            total: table.data.length,
          })}
        </div>
      )}
    </div>
  );
}
